import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { skipLoader } from '@rsm/shared/loader';
import { environment } from 'src/environments/environment';

/** Returned by POST /billings/pix/wallet/company */
export type WalletPixMovement = {
  qrCode: string;
  transactionId: string;
  transactionStatus: string;
  dateExpire: string;
  movementId: string;
  value: number;
};

/** Returned by GET /billings/pix/wallet/company/{contextId}/check-payment */
export type WalletPaymentStatus = {
  /** PENDING | CONFIRMED | NOT_FOUND */
  status: 'PENDING' | 'CONFIRMED' | 'NOT_FOUND';
  /** PIX QR-code string — only when PENDING */
  qrCode?: string;
  /** QR-code expiry timestamp — only when PENDING */
  expiresAt?: string;
  /** Updated wallet balance after credit — only when CONFIRMED */
  newBalance?: number;
};

/** Uma ação no boleto de campanha (espelha ActionsTicketQrcodeTO no backend). */
export interface BoletoCampaignAction {
  name: string;
  qtdAgents: number;
  value: number;
}

/** Payload do boleto de campanha. */
export interface CampaignBoletoRequest {
  campaingId: string;
  nameCompany?: string;
  cnpjCompany?: string;
  nameCampaign?: string;
  actions: BoletoCampaignAction[];
}

/**
 * Dispara o download de um PDF (Blob) no navegador, como um anexo.
 * Centraliza a lógica pra não duplicar nos 3 checkouts.
 */
export function saveBlobAsPdf(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.URL.revokeObjectURL(url);
}

export interface PointCommercialPolicy {
  id?: string;
  purchasePoints: number;
  purchaseValue: number;
  salesPoints: number;
  salesValue: number;
  spreadMinPercent: number;
  purchaseUnitValue?: number;
  salesUnitValue?: number;
  spreadPercent?: number;
  dateRegistration?: string;
  dateUpdate?: string;
}

/**
 *
 */
@Injectable({
  providedIn: 'root',
})
export class CorporativeClientService {
  /**
   * @param http
   */
  constructor(private http: HttpClient) {}

  /**
   *
   * @returns
   */
  public getStates(): Observable<any> {
    return this.http.get<any>(`${environment.corp.url}/states`);
  }

  /**
   *
   * @returns
   */
  public getCities(idState: any): Observable<any> {
    return this.http.get<any>(`${environment.corp.url}/cities/` + idState);
  }

  /**
   *
   * @returns
   */
  public getGenders(): Observable<any> {
    return this.http.get<any>(`${environment.corp.url}/genders`);
  }

  /**
   *
   * @returns
   */
  public getAges(): Observable<any> {
    return this.http.get<any>(`${environment.corp.url}/age`);
  }

  public getWalletBalance(contextId: string) {
    return this.http.get<{
      valueBalance: number;
      dateBalance: string;
      haveBalanceWallet: boolean;
      id: string;
    }>(`${environment.corp.url}/wallet/company/context/${contextId}/balance`);
  }

  public getPurchaseValue({
    contextid,
    qtdAgents,
    qtdPoints,
  }: {
    contextid: string;
    qtdAgents: number;
    qtdPoints: number;
  }) {
    return this.http.get<number>(
      `${environment.corp.url}/conversion/purchase/value/context/${contextid}`,
      { params: { qtdAgents, qtdPoints } }
    );
  }

  public getPointCommercialPolicy(): Observable<PointCommercialPolicy> {
    return this.http.get<PointCommercialPolicy>(
      `${environment.corp.url}/point-commercial-policy/current`
    );
  }

  public updatePointCommercialPolicy(
    body: PointCommercialPolicy
  ): Observable<PointCommercialPolicy> {
    return this.http.put<PointCommercialPolicy>(
      `${environment.corp.url}/point-commercial-policy/current`,
      body
    );
  }

  public getAddressFromCEP(cep: string) {
    return this.http.get<any>(`${environment.corp.url}/address/cep/${cep}`);
  }

  public consumeCampaignCredits(body: {
    contextId: string;
    campaignId: string;
    billingValue: number;
  }) {
    return this.http.post<any>(
      `${environment.corp.url}/billings/campaign/consume`,
      body
    );
  }

  public sendValidationEmail(payload: any): Observable<any> {
    return this.http.post(
      `${environment.corp.url}/emails/send-template`,
      payload
    );
  }

  public getCreditHistory(contextId: string) {
    return this.http.get<any[]>(
      `${environment.corp.url}/wallet/company/context/${contextId}/credit/history`
    );
  }

  public getCompanyMovements(contextId: string) {
    return this.http.get<any[]>(
      `${environment.corp.url}/wallet/company/context/${contextId}/movements`
    );
  }

  /**
   * Generates a real BS2 PIX QR code for a company wallet-credit top-up.
   * Replaces the fake buildPixCode() that existed in HomeCheckoutComponent.
   */
  public createWalletCreditPix(body: {
    contextId: string;
    billingValue: number;
    billingLicensesValue: number;
    billingCreditsValue: number;
    licensesQuantity?: number;
  }) {
    return this.http.post<WalletPixMovement>(
      `${environment.corp.url}/billings/pix/wallet/company`,
      body
    );
  }

  /**
   * Checks the real-time payment status of a pending wallet-credit PIX.
   * Called by the polling loop in HomeCheckoutComponent every 5–15 s.
   */
  public checkWalletCreditPayment(contextId: string) {
    return this.http.get<WalletPaymentStatus>(
      `${environment.corp.url}/billings/pix/wallet/company/${contextId}/check-payment`,
      { context: skipLoader() }
    );
  }

  /**
   * Compra licenças usando o saldo já disponível na carteira (sem PIX).
   * Concede as licenças no mission-api e debita o valor da carteira.
   */
  public purchaseLicensesWithBalance(
    contextId: string,
    licensesQuantity: number,
    amount: number
  ) {
    return this.http.post<{ valueBalance: number }>(
      `${environment.corp.url}/wallet/company/context/${contextId}/purchase-licenses-with-balance`,
      { licensesQuantity, amount }
    );
  }

  // ---------------------------------------------------------------------------
  // Boleto (bolepix) — PDF que estampa o MESMO QR Code PIX da transação.
  // Os 3 endpoints retornam application/pdf; baixamos como Blob.
  // ---------------------------------------------------------------------------

  /** Boleto de crédito de carteira (recarga na home). */
  public downloadWalletBoleto(movementID: string): Observable<Blob> {
    return this.http.post(
      `${environment.corp.url}/report/ticket/qrcode/wallet`,
      { movementID },
      { responseType: 'blob' }
    );
  }

  /** Boleto de checkout de campanha. */
  public downloadCampaignBoleto(body: CampaignBoletoRequest): Observable<Blob> {
    return this.http.post(
      `${environment.corp.url}/report/ticket/qrcode/campaign`,
      body,
      { responseType: 'blob' }
    );
  }

  /** Boleto de contratação de plano (cadastro de empresa). */
  public downloadCompanyBoleto(body: {
    movementID: string;
    planCompanyID: string;
  }): Observable<Blob> {
    return this.http.post(
      `${environment.corp.url}/report/ticket/qrcode/company`,
      body,
      { responseType: 'blob' }
    );
  }

  transferBalanceWallet(body: any): Observable<void> {
    const url = `${environment.corp.url}/wallet/company/transfer-balance-wallet`;
    return this.http.post<void>(url, body);
  }
}
